/**
 * Xero-Commission-Gamification Mycelium Connector
 *
 * Bridges Xero accounting events to commission calculations and gamification rewards.
 * Implements automatic commission calculation and XP awards when invoices are paid.
 *
 * Key Features:
 * - Listens to Xero webhook events (invoice_created, invoice_paid, payment_received)
 * - Triggers commission calculations automatically
 * - Awards XP for commission-eligible transactions
 * - Publishes commission_calculated events for team notifications
 * - Integrates with existing Activity-Gamification connector for XP
 *
 * Flow Example:
 * 1. Xero webhook: INVOICE.UPDATE (status: PAID)
 *    → Connector detects paid invoice
 *    → Calculates commissions for sales team
 *    → Publishes commission_calculated event
 *    → Activity-Gamification awards XP (already configured: invoice_paid: 100 XP)
 *
 * @module mycelium-xero-commission-gamification
 * @since 2026-02-20
 */

import { myceliumEventBackbone, type MyceliumEvent } from "./mycelium-event-backbone.service";
import { getMyceliumTransport } from "./mycelium-transport-registry";
import { logger } from "../lib/logger";
import { typedSupabase } from "../lib/typed-supabase";

// ============================================================================
// CONFIGURATION
// ============================================================================

/**
 * Xero event types that trigger commission calculations
 */
const COMMISSION_TRIGGER_EVENTS = {
  // Invoice events
  INVOICE_CREATED: 'invoice_created',
  INVOICE_PAID: 'invoice_paid',
  INVOICE_UPDATED: 'invoice_updated',

  // Payment events
  PAYMENT_RECEIVED: 'payment_received',
  PAYMENT_CREATED: 'payment_created',

  // Transaction events (from accounting portal)
  TRANSACTION_CREATED: 'transaction_created',
  TRANSACTION_COMPLETED: 'transaction_completed',
} as const;

/**
 * Commission calculation types
 */
const COMMISSION_TYPES = {
  INVOICE_PAYMENT: 'invoice_payment',
  RECURRING_PAYMENT: 'recurring_payment',
  ONE_TIME_SALE: 'one_time_sale',
  UPSELL: 'upsell',
  RENEWAL: 'renewal',
} as const;

/**
 * Minimum invoice amount to trigger commission (configurable)
 */
const CONFIG = {
  minInvoiceAmount: 0, // Trigger for all amounts
  commissionDelay: 5000, // 5 second delay for batch processing
  retryAttempts: 3,
  retryDelay: 2000,
} as const;

// ============================================================================
// TYPES
// ============================================================================

interface XeroInvoicePayload {
  invoiceId: string;
  invoiceNumber?: string;
  contactId?: string;
  contactName?: string;
  clientId?: string;
  totalAmount: number;
  amountPaid?: number;
  status: string;
  dueDate?: string;
  paidDate?: string;
  tenantId?: string;
  currencyCode?: string;
}

interface CommissionResult {
  transactionId: string;
  totalCommission: number;
  splits: {
    userId: string;
    userName?: string;
    role: string;
    amount: number;
    percentage: number;
  }[];
  metadata: {
    invoiceId?: string;
    clientId?: string;
    transactionType: string;
  };
}

// ============================================================================
// CONNECTOR SERVICE
// ============================================================================

export class XeroCommissionGamificationConnector {
  private initialized = false;
  private pendingCalculations = new Map<string, NodeJS.Timeout>();

  /**
   * Initialize connector and subscribe to events
   */
  async initialize(): Promise<void> {
    if (this.initialized) {
      logger.debug("XeroCommissionGamificationConnector already initialized");
      return;
    }

    logger.info("💰 Xero-Commission-Gamification connector initializing");

    // Subscribe to accounting domain events (from Xero)
    myceliumEventBackbone.subscribe({
      name: "XeroCommissionGamificationConnector-Accounting",
      domains: ["accounting", "integration"],
      eventTypes: Object.values(COMMISSION_TRIGGER_EVENTS),
      handler: this.handleAccountingEvent.bind(this),
      priority: 8, // High priority for financial events
    });

    // Subscribe to Xero webhook events specifically
    myceliumEventBackbone.subscribe({
      name: "XeroCommissionGamificationConnector-Xero",
      domains: ["integration"],
      eventTypes: ["xero_webhook_received", "xero_invoice_paid"],
      handler: this.handleXeroWebhookEvent.bind(this),
      priority: 9, // Highest priority for direct webhook events
    });

    this.initialized = true;
    logger.info("✅ Xero-Commission-Gamification connector ready");
    logger.info("💡 Invoice payments will auto-calculate commissions and award XP");
  }

  /**
   * Handle accounting domain events (invoices, payments, transactions)
   */
  private async handleAccountingEvent(event: MyceliumEvent): Promise<void> {
    const { type, userId, payload } = event;

    logger.debug("💰 Accounting event received", {
      eventId: event.id,
      type,
      userId,
    });

    try {
      // Check if this event should trigger commission calculation
      if (!this.shouldTriggerCommission(type, payload)) {
        logger.debug("Event does not trigger commission", { type });
        return;
      }

      // Parse invoice/transaction data
      const invoiceData = this.parseInvoiceData(payload);
      if (!invoiceData) {
        logger.debug("Could not parse invoice data", { payload });
        return;
      }

      // Check minimum amount threshold
      if (invoiceData.totalAmount < CONFIG.minInvoiceAmount) {
        logger.debug("Invoice below minimum threshold", {
          amount: invoiceData.totalAmount,
          threshold: CONFIG.minInvoiceAmount,
        });
        return;
      }

      logger.info("🎯 Triggering commission calculation", {
        invoiceId: invoiceData.invoiceId,
        amount: invoiceData.totalAmount,
        clientId: invoiceData.clientId,
      });

      // Trigger commission calculation (with debounce for multiple events)
      await this.scheduleCommissionCalculation(invoiceData, event);
    } catch (error) {
      logger.error("Error handling accounting event", {
        eventId: event.id,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    }
  }

  /**
   * Handle Xero webhook events directly from webhook processor
   */
  private async handleXeroWebhookEvent(event: MyceliumEvent): Promise<void> {
    const { type, payload } = event;

    logger.debug("📨 Xero webhook event received", {
      eventId: event.id,
      type,
      eventCategory: payload.eventCategory,
      eventType: payload.eventType,
    });

    try {
      // Extract Xero event data
      const eventCategory = payload.eventCategory as string;
      const eventType = payload.eventType as string;
      const resourceId = payload.resourceId as string;

      // Only process invoice events that are paid
      if (eventCategory === 'INVOICE' && eventType === 'UPDATE') {
        // Fetch full invoice details from webhook event
        const invoiceData = await this.fetchInvoiceDetailsFromPayload(payload);

        if (invoiceData && invoiceData.status === 'PAID') {
          logger.info("💳 Invoice marked as PAID from Xero webhook", {
            invoiceId: invoiceData.invoiceId,
            amount: invoiceData.totalAmount,
          });

          // Schedule commission calculation
          await this.scheduleCommissionCalculation(invoiceData, event);
        }
      } else if (eventCategory === 'PAYMENT') {
        // Payment received - might trigger commission for associated invoice
        logger.info("💵 Payment received from Xero", {
          paymentId: resourceId,
        });

        // Fetch payment details and associated invoices
        const paymentData = await this.fetchPaymentDetailsFromPayload(payload);
        if (paymentData && paymentData.invoiceId) {
          const invoiceData = await this.fetchInvoiceById(paymentData.invoiceId);
          if (invoiceData && invoiceData.status === 'PAID') {
            await this.scheduleCommissionCalculation(invoiceData, event);
          }
        }
      }
    } catch (error) {
      logger.error("Error handling Xero webhook event", {
        eventId: event.id,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    }
  }

  /**
   * Schedule commission calculation (with debounce)
   */
  private async scheduleCommissionCalculation(
    invoiceData: XeroInvoicePayload,
    sourceEvent: MyceliumEvent
  ): Promise<void> {
    const key = invoiceData.invoiceId;

    // Clear any pending calculation for this invoice
    if (this.pendingCalculations.has(key)) {
      clearTimeout(this.pendingCalculations.get(key)!);
    }

    // Schedule new calculation with delay (debounce)
    const timeoutId = setTimeout(async () => {
      this.pendingCalculations.delete(key);
      await this.calculateAndPublishCommission(invoiceData, sourceEvent);
    }, CONFIG.commissionDelay);

    this.pendingCalculations.set(key, timeoutId);
  }

  /**
   * Calculate commission and publish events
   */
  private async calculateAndPublishCommission(
    invoiceData: XeroInvoicePayload,
    sourceEvent: MyceliumEvent
  ): Promise<void> {
    try {
      logger.info("💵 Calculating commission", {
        invoiceId: invoiceData.invoiceId,
        amount: invoiceData.totalAmount,
      });

      // Determine commission type
      const commissionType = this.determineCommissionType(invoiceData);

      // Calculate commission splits
      const commissionResult = await this.calculateCommissionSplits(
        invoiceData,
        commissionType
      );

      if (!commissionResult) {
        logger.warn("Commission calculation returned null", {
          invoiceId: invoiceData.invoiceId,
        });
        return;
      }

      logger.info("✅ Commission calculated successfully", {
        invoiceId: invoiceData.invoiceId,
        totalCommission: commissionResult.totalCommission,
        splits: commissionResult.splits.length,
      });

      // Publish commission_calculated event for team notifications
      await getMyceliumTransport().publish({
        domain: "accounting",
        type: "commission_calculated",
        userId: commissionResult.splits[0]?.userId, // Primary beneficiary
        payload: {
          invoiceId: invoiceData.invoiceId,
          invoiceNumber: invoiceData.invoiceNumber,
          clientId: invoiceData.clientId,
          clientName: invoiceData.contactName,
          totalAmount: invoiceData.totalAmount,
          totalCommission: commissionResult.totalCommission,
          commissionType,
          splits: commissionResult.splits,
          calculatedAt: new Date().toISOString(),
          sourceEventId: sourceEvent.id,
        },
      });

      // Publish individual commission_earned events for each recipient
      // (Activity-Gamification connector will pick these up and award XP)
      for (const split of commissionResult.splits) {
        await getMyceliumTransport().publish({
          domain: "gamification",
          type: "commission_earned",
          userId: split.userId,
          payload: {
            commissionAmount: split.amount,
            commissionPercentage: split.percentage,
            role: split.role,
            invoiceId: invoiceData.invoiceId,
            clientId: invoiceData.clientId,
            sourceEventId: sourceEvent.id,
          },
        });
      }

      // Note: Activity-Gamification connector already has invoice_paid: 100 XP mapping
      // So XP will be awarded automatically when commission_earned events are published
    } catch (error) {
      logger.error("Error calculating commission", {
        invoiceId: invoiceData.invoiceId,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    }
  }

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================

  /**
   * Check if event should trigger commission calculation
   */
  private shouldTriggerCommission(eventType: string, payload: Record<string, unknown>): boolean {
    // Invoice paid events
    if (eventType === COMMISSION_TRIGGER_EVENTS.INVOICE_PAID) {
      return true;
    }

    // Invoice updated to PAID status
    if (eventType === COMMISSION_TRIGGER_EVENTS.INVOICE_UPDATED) {
      const status = payload.status as string;
      return status === 'PAID' || status === 'AUTHORISED';
    }

    // Payment received events
    if (eventType === COMMISSION_TRIGGER_EVENTS.PAYMENT_RECEIVED ||
        eventType === COMMISSION_TRIGGER_EVENTS.PAYMENT_CREATED) {
      return true;
    }

    // Transaction completed (from accounting portal)
    if (eventType === COMMISSION_TRIGGER_EVENTS.TRANSACTION_COMPLETED) {
      const type = payload.type as string;
      return type === 'income' || type === 'sale';
    }

    return false;
  }

  /**
   * Parse invoice data from event payload
   */
  private parseInvoiceData(payload: Record<string, unknown>): XeroInvoicePayload | null {
    try {
      // Direct invoice data
      if (payload.invoiceId || payload.invoice_id) {
        return {
          invoiceId: (payload.invoiceId || payload.invoice_id) as string,
          invoiceNumber: payload.invoiceNumber as string || payload.invoice_number as string,
          contactId: payload.contactId as string || payload.contact_id as string,
          contactName: payload.contactName as string || payload.contact_name as string,
          clientId: payload.clientId as string || payload.client_id as string,
          totalAmount: (payload.totalAmount || payload.total_amount || payload.amount || 0) as number,
          amountPaid: (payload.amountPaid || payload.amount_paid) as number,
          status: (payload.status as string || '').toUpperCase(),
          dueDate: payload.dueDate as string || payload.due_date as string,
          paidDate: payload.paidDate as string || payload.paid_date as string,
          tenantId: payload.tenantId as string || payload.tenant_id as string,
          currencyCode: payload.currencyCode as string || payload.currency_code as string || 'USD',
        };
      }

      // Nested invoice data
      if (payload.invoice) {
        const invoice = payload.invoice as Record<string, unknown>;
        return this.parseInvoiceData(invoice);
      }

      return null;
    } catch (error) {
      logger.error("Error parsing invoice data", { error, payload });
      return null;
    }
  }

  /**
   * Fetch invoice details from Xero webhook payload
   */
  private async fetchInvoiceDetailsFromPayload(
    payload: Record<string, unknown>
  ): Promise<XeroInvoicePayload | null> {
    try {
      const resourceId = payload.resourceId as string;
      const tenantId = payload.tenantId as string;

      if (!resourceId) {
        return null;
      }

      // Query our local database for invoice details
      // (assuming xero sync service has already synced invoice data)
      const { data, error } = await typedSupabase
        .from('accounting_transactions')
        .select('*')
        .eq('external_id', resourceId)
        .eq('source', 'xero')
        .single();

      if (error || !data) {
        logger.warn("Invoice not found in local database", { resourceId, error });
        return null;
      }

      return {
        invoiceId: data.external_id || data.id,
        invoiceNumber: data.reference,
        contactId: data.external_contact_id,
        contactName: data.contact_name,
        clientId: data.client_id,
        totalAmount: parseFloat(data.amount as string) || 0,
        status: data.status?.toUpperCase() || '',
        dueDate: data.due_date,
        paidDate: data.paid_date,
        tenantId: tenantId,
        currencyCode: data.currency,
      };
    } catch (error) {
      logger.error("Error fetching invoice details", { error, payload });
      return null;
    }
  }

  /**
   * Fetch payment details from payload
   */
  private async fetchPaymentDetailsFromPayload(
    payload: Record<string, unknown>
  ): Promise<{ paymentId: string; invoiceId?: string } | null> {
    try {
      const resourceId = payload.resourceId as string;

      // Payment events in Xero include invoice references
      // For now, return basic structure (would need Xero API integration for full details)
      return {
        paymentId: resourceId,
        invoiceId: payload.invoiceId as string,
      };
    } catch (error) {
      logger.error("Error fetching payment details", { error, payload });
      return null;
    }
  }

  /**
   * Fetch invoice by ID from local database
   */
  private async fetchInvoiceById(invoiceId: string): Promise<XeroInvoicePayload | null> {
    try {
      const { data, error } = await typedSupabase
        .from('accounting_transactions')
        .select('*')
        .or(`id.eq.${invoiceId},external_id.eq.${invoiceId}`)
        .single();

      if (error || !data) {
        return null;
      }

      return {
        invoiceId: data.external_id || data.id,
        invoiceNumber: data.reference,
        clientId: data.client_id,
        contactName: data.contact_name,
        totalAmount: parseFloat(data.amount as string) || 0,
        status: data.status?.toUpperCase() || '',
        dueDate: data.due_date,
        paidDate: data.paid_date,
        currencyCode: data.currency,
      };
    } catch (error) {
      logger.error("Error fetching invoice by ID", { error, invoiceId });
      return null;
    }
  }

  /**
   * Determine commission type based on invoice data
   */
  private determineCommissionType(invoiceData: XeroInvoicePayload): string {
    // This is a simplified version - real implementation would check:
    // - Client history (new vs existing)
    // - Product/service type
    // - Contract terms
    // - etc.

    // For now, default to invoice_payment
    return COMMISSION_TYPES.INVOICE_PAYMENT;
  }

  /**
   * Calculate commission splits for the invoice
   */
  private async calculateCommissionSplits(
    invoiceData: XeroInvoicePayload,
    commissionType: string
  ): Promise<CommissionResult | null> {
    try {
      if (!invoiceData.clientId) {
        logger.warn("Cannot calculate commission without clientId", {
          invoiceId: invoiceData.invoiceId,
        });
        return null;
      }

      // Query client to get associated team members
      const { data: client, error: clientError } = await typedSupabase
        .from('clients')
        .select('id, account_manager_id, sales_rep_id, closer_id')
        .eq('id', invoiceData.clientId)
        .single();

      if (clientError || !client) {
        logger.warn("Client not found for commission calculation", {
          clientId: invoiceData.clientId,
          error: clientError,
        });
        return null;
      }

      // Default commission structure (can be overridden by team/tier settings)
      const baseCommission = invoiceData.totalAmount * 0.10; // 10% default
      const splits: CommissionResult['splits'] = [];

      // Account Manager: 40% of commission
      if (client.account_manager_id) {
        const { data: manager } = await typedSupabase
          .from('profiles')
          .select('id, full_name')
          .eq('id', client.account_manager_id)
          .single();

        if (manager) {
          splits.push({
            userId: manager.id,
            userName: manager.full_name,
            role: 'account_manager',
            amount: baseCommission * 0.4,
            percentage: 40,
          });
        }
      }

      // Sales Rep: 35% of commission
      if (client.sales_rep_id) {
        const { data: salesRep } = await typedSupabase
          .from('profiles')
          .select('id, full_name')
          .eq('id', client.sales_rep_id)
          .single();

        if (salesRep) {
          splits.push({
            userId: salesRep.id,
            userName: salesRep.full_name,
            role: 'sales_rep',
            amount: baseCommission * 0.35,
            percentage: 35,
          });
        }
      }

      // Closer: 25% of commission
      if (client.closer_id) {
        const { data: closer } = await typedSupabase
          .from('profiles')
          .select('id, full_name')
          .eq('id', client.closer_id)
          .single();

        if (closer) {
          splits.push({
            userId: closer.id,
            userName: closer.full_name,
            role: 'closer',
            amount: baseCommission * 0.25,
            percentage: 25,
          });
        }
      }

      // If no team members found, assign full commission to account owner
      if (splits.length === 0 && client.account_manager_id) {
        const { data: owner } = await typedSupabase
          .from('profiles')
          .select('id, full_name')
          .eq('id', client.account_manager_id)
          .single();

        if (owner) {
          splits.push({
            userId: owner.id,
            userName: owner.full_name,
            role: 'owner',
            amount: baseCommission,
            percentage: 100,
          });
        }
      }

      return {
        transactionId: invoiceData.invoiceId,
        totalCommission: baseCommission,
        splits,
        metadata: {
          invoiceId: invoiceData.invoiceId,
          clientId: invoiceData.clientId,
          transactionType: commissionType,
        },
      };
    } catch (error) {
      logger.error("Error calculating commission splits", {
        invoiceId: invoiceData.invoiceId,
        error: error instanceof Error ? error.message : "Unknown error",
      });
      return null;
    }
  }

  /**
   * Cleanup pending calculations on disposal
   */
  async dispose(): Promise<void> {
    for (const [key, timeoutId] of this.pendingCalculations.entries()) {
      clearTimeout(timeoutId);
      this.pendingCalculations.delete(key);
    }
    this.initialized = false;
    logger.info("🧹 Xero-Commission-Gamification connector disposed");
  }
}

// ============================================================================
// SINGLETON INSTANCE
// ============================================================================

export const xeroCommissionGamificationConnector = new XeroCommissionGamificationConnector();
